import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@structural-climbing/ui'

interface RemoveStaffModalProps {
  staffId: string
  staffName: string
  gymName: string
  onClose: () => void
  onRemoved: (staffId: string) => void
}

/**
 * Asks before taking someone off this gym's team. The Worker removes only this
 * gym's record; their other gyms and their own membership stay.
 */
export function RemoveStaffModal({
  staffId,
  staffName,
  gymName,
  onClose,
  onRemoved,
}: RemoveStaffModalProps) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const [removing, setRemoving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleRemove() {
    setRemoving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const resp = await fetch(`${apiUrl}/staff/${encodeURIComponent(staffId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (resp.ok) {
        onRemoved(staffId)
        return
      }
      setError(resp.status === 409 ? t('team.errorRemoveOwner') : t('team.errorRemove'))
    } catch {
      setError(t('team.errorRemove'))
    } finally {
      setRemoving(false)
    }
  }

  return (
    <Modal
      isOpen
      onOpenChange={(open) => {
        if (!open && !removing) onClose()
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>{t('team.removeTitle', { name: staffName })}</ModalHeader>
            <ModalBody>
              <p className="text-sm">{t('team.removeBody', { name: staffName, gym: gymName })}</p>
              <p className="text-sm text-foreground/60">{t('team.removeKeeps')}</p>
              {error && <p className="text-sm text-danger">{error}</p>}
            </ModalBody>
            <ModalFooter>
              <Button variant="flat" onPress={onClose} isDisabled={removing}>
                {t('common.cancel')}
              </Button>
              <Button color="danger" onPress={() => void handleRemove()} isLoading={removing}>
                {t('team.removeConfirm')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
